// ────────────────────────────
// src/components/OverviewSection.jsx
// ────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "animate.css";
import "./OverviewSection.css";

/* ─── Swiper sólo en móvil ─── */
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

/* ─── capturas ─── */
import dashboardImgClients  from "../assets/images/dashboard-screenshots/dashboard-clients.png";
import dashboardImgRoutines from "../assets/images/dashboard-screenshots/dashboard-routines.png";
import dashboardImgDiets    from "../assets/images/dashboard-screenshots/dashboard-diets-options.png";
import dashboardImgProgress from "../assets/images/dashboard-screenshots/dashboard-progress-top.png";

import iphoneMockup1 from "../assets/images/mobile-screenshots/mobile-home-dark.png";
import iphoneMockup2 from "../assets/images/mobile-screenshots/mobile-routine-dark.png";
import iphoneMockup3 from "../assets/images/mobile-screenshots/mobile-diet-dark.png";
import iphoneMockup4 from "../assets/images/mobile-screenshots/mobile-progress-dark.png";

import logoZ from "../assets/images/logos/logoZ.png";
import logoZASFIT from "../assets/images/logos/logoZASFIT.png";

import partner1 from "../assets/images/partners/partner1.png";
import partner2 from "../assets/images/partners/partner2.png";

import remark_shape from "../assets/images/shapes/section-title-circle-yellow.png";

/* ——— arrays de imágenes ——— */
const DASHBOARD_SHOTS = [
  dashboardImgClients,
  dashboardImgRoutines,
  dashboardImgDiets,
  dashboardImgProgress,
];

const DASHBOARD_ALTS = [
  "Panel de clientes – aplicación de entrenamiento personal Zasfit",
  "Rutinas – software para entrenadores personales Zasfit",
  "Dietas personalizadas – aplicación de entrenamiento personal Zasfit",
  "Gráfica de progreso – app para entrenadores personales Zasfit"
];

const MOBILE_SHOTS = [
  iphoneMockup1, 
  iphoneMockup2, 
  iphoneMockup3, 
  iphoneMockup4
];

const MOBILE_ALTS = [
  "Pantalla de inicio móvil – aplicación de entrenamiento personal Zasfit",
  "Vista de rutina móvil – software para entrenadores personales Zasfit",
  "Plan de dieta móvil – aplicación para entrenadores personales Zasfit",
  "Seguimiento de progreso móvil – app de entrenamiento personal Zasfit"
];


/* ——— fade in/out con Framer Motion ——— */
function RotatingImage({ imgs, alts, className, delay = 4300, fade = 2 }) {
  const [current, setCurrent] = useState(0);
  const timer = useRef();

  useEffect(() => {
    timer.current && clearTimeout(timer.current);
    timer.current = setTimeout(
      () => setCurrent(i => (i + 1) % imgs.length),
      delay
    );
    return () => clearTimeout(timer.current);
  }, [current, imgs.length, delay]);

  return (
    <div className="rotating-wrapper">
      <AnimatePresence initial={false}>
        <motion.img
          key={`${imgs[current]}-${current}`}
          src={imgs[current]}
          alt={alts[current]}              
          className={`rotating-shot ${className}`}
          initial={{ opacity: 1, zIndex: 0 }}
          animate={{ opacity: 1, zIndex: 0 }}
          exit={{ opacity: 0, zIndex: 1 }}
          transition={{ duration: fade, ease: "easeInOut" }}
        />
      </AnimatePresence>
    </div>
  );
}

function CountUp({ to = 100, duration = 1200, prefix = "+", className = "" }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce) { setVal(to); return; }

    let frameId;
    const start = performance.now();
    const easeOutCubic = t => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.round(to * easeOutCubic(p)));
      if (p < 1) frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [to, duration]);

  return <span className={className} aria-live="polite">{prefix}{val}</span>;
}


/* ——— componente principal ——— */
export default function OverviewSection() {
  const isMobile = useMediaQuery("(max-width: 991.98px)"); // < 992 px

  const countRef = useRef(null);
  const playCount = useOnScreenOnce(countRef, {
    rootMargin: "0px 0px -12% 0px",
    threshold: 0.4,
  });

  const swiperProps = isMobile
    ? {
        // allowTouchMove: false,       
        // touchStartPreventDefault: false,
        grabCursor: false,
      }
    : {
        // allowTouchMove: true,        
        grabCursor: true,
      };

  return (
    <section className="overview-section rel bg-gray pt-50 pb-0 rpb-0">
      <div className="container-xxl position-relative">
        {/* ─────────── FILA PRINCIPAL ─────────── */}
        <div
          className="
            overview-row justify-content-center align-items-start
            animate__animated animate__fadeInUp
          "
          style={{ "--animate-delay": "1.2s" }}
        >
          {/* ① Dashboard → siempre Framer Motion */}
          <div className="overview-dashboard-col">
            <RotatingImage
              imgs={DASHBOARD_SHOTS}
              alts={DASHBOARD_ALTS}
              className="overview-dashboard"
            />
          </div>

          {/* ② Mock‑up iPhone → Swiper sólo en móvil */}
          <div className="overview-mobile-col">
            {isMobile ? (
              <Swiper
                className="iphone-swiper swiper--noTouch"
                modules={[Autoplay, Pagination]}
                slidesPerView={3}          /* ⬅︎ 3 visibles a la vez */
                loop
                
                autoplay={{
                  delay: 2400,
                  disableOnInteraction: false,
                }}
                speed={700}
                spaceBetween={16}
                {...swiperProps}
              >
                {MOBILE_SHOTS.map((src, i) => (
                  <SwiperSlide key={i}>
                    <img src={src} alt={`mobile screenshot ${i + 1}`} />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              /* En desktop seguimos con la animación de siempre */
              <RotatingImage
                imgs={MOBILE_SHOTS}
                alts={MOBILE_ALTS}
                className="iphone-mockup"
              />
            )}
          </div>
        </div>

        {/* ───── NUEVO: Trust + Partners, con el mismo desplazamiento ───── */}
        <div className="ovw-stack">
          <div className="ovw-trust">
            <div className="ovw-count">
              <span className="ovw-count__numwrap" ref={countRef}>
                <img className="ovw-count__remark" src={remark_shape} alt="" aria-hidden="true" />
                {playCount
                  ? <CountUp to={500} duration={1300} prefix="+" className="ovw-badge-num" />
                  : <span className="ovw-badge-num">+0</span>}
              </span>
              <span className="ovw-badge-label">ENTRENADORES</span>
            </div>


            <span className="ovw-trust__text">YA CONFÍAN&nbsp;EN</span>

            <div className="ovw-logos">
              <img className="ovw-trust__logo_z" src={logoZ} alt="logoZ" />
              <img className="ovw-trust__logo" src={logoZASFIT} alt="logoZASFIT" />
            </div>
          </div>

          <div className="ovw-partners">
            <div className="ovw-partners__card">
              <div className="ovw-partners__label">PARTNERS OFICIALES</div>
                <div className="ovw-partners__logos">
                  <a
                    href="https://consciousathlete.es/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Conscious Athlete"
                    className="ovw-partners__logo-link"
                  >
                    <img
                      src={partner1}
                      alt="Conscious Athlete"
                      className="ovw-partners__logo ovw-logo--conscious"
                    />
                  </a>

                  <a
                    href="https://evolutiontrainingclub.es/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Evolution Training Club"
                    className="ovw-partners__logo-link"
                  >
                    <img
                      src={partner2}
                      alt="Evolution Training Club"
                      className="ovw-partners__logo ovw-logo--evolution"
                    />
                  </a>
                </div>

            </div>
          </div>

        </div>
      </div>

      {/* shape decorativo existente */}
      <div className="bg-yellow-shape" />
    </section>
  );
}

function useOnScreenOnce(ref, {
  root = null,
  rootMargin = "0px 0px -15% 0px",   // empieza un pelín antes de entrar
  threshold = 0.25                   // ~35% visible
} = {}) {
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    if (seen) return;                            // ya disparado, no observar más
    const el = ref.current;
    if (!el) return;

    // Fallback: si no hay IntersectionObserver, anima ya
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setSeen(true);
      return;
    }

    const obs = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        setSeen(true);
        obs.unobserve(el);                      // deja de observar (solo una vez)
      }
    }, { root, rootMargin, threshold });

    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, root, rootMargin, threshold, seen]);

  return seen;
}


/* ——— hook local (o muévelo a /hooks) ——— */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = e => setMatches(e.matches);
    mql.addEventListener?.("change", onChange) || mql.addListener(onChange);
    return () =>
      mql.removeEventListener?.("change", onChange) ||
      mql.removeListener(onChange);
  }, [query]);

  return matches;
}
